import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { FindingCard } from '@/components/findings/finding-card';
import { FindingFilters } from '@/components/findings/finding-filters';
import { EmptyState } from '@/components/shared/empty-state';
import { Card } from '@/components/ui/card';
import { Search, Shield } from 'lucide-react';
import type { Finding, Severity, FindingStatus } from '@nirvik/shared';
import { useOrgContext } from '@/providers/org-provider';
import { useAuth } from '@/providers/auth-provider';

export function FindingsPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();
  const [selectedSeverities, setSelectedSeverities] = useState<Severity[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<FindingStatus[]>([]);
  const queryClient = useQueryClient();
  const { currentOrg } = useOrgContext();
  const { user } = useAuth();
  // Delete is gated to org admins or platform admins. Dismiss /
  // accept-risk / mark-fixed remain available to anyone with member role
  // (handled server-side; the dropdown only renders when handlers fire).
  const canDelete = (currentOrg?.role === 'admin') || !!user?.isAdmin;

  const { data, isLoading } = useQuery({
    queryKey: ['findings', orgId, portfolioId, projectId],
    queryFn: () => apiClient.listFindings(orgId!, portfolioId!, projectId!),
    enabled: !!orgId && !!portfolioId && !!projectId,
  });

  const allFindings = (data?.data as Finding[] | undefined) || [];

  const updateStatus = useMutation({
    mutationFn: (vars: { findingId: string; status: 'dismissed' | 'accepted-risk' | 'fixed' }) =>
      apiClient.updateFinding(orgId!, portfolioId!, projectId!, vars.findingId, vars.status),
    onSuccess: (_res, vars) => {
      toast.success(`Marked as ${vars.status}`);
      queryClient.invalidateQueries({ queryKey: ['findings', orgId, portfolioId, projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', orgId, portfolioId, projectId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteFinding = useMutation({
    mutationFn: (findingId: string) => apiClient.deleteFinding(orgId!, portfolioId!, projectId!, findingId),
    onSuccess: () => {
      toast.success('Finding deleted');
      queryClient.invalidateQueries({ queryKey: ['findings', orgId, portfolioId, projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', orgId, portfolioId, projectId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggleSeverity = (sev: Severity) => {
    setSelectedSeverities((prev) =>
      prev.includes(sev) ? prev.filter((s) => s !== sev) : [...prev, sev]
    );
  };

  const toggleStatus = (status: FindingStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const filteredFindings = useMemo(() => {
    return allFindings.filter((f) => {
      if (selectedSeverities.length > 0 && !selectedSeverities.includes(f.severity)) return false;
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(f.status)) return false;
      return true;
    });
  }, [allFindings, selectedSeverities, selectedStatuses]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-4 bg-gray-100 dark:bg-muted rounded w-32" />
        <div className="h-8 bg-gray-200 dark:bg-muted rounded w-48" />
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-muted rounded w-16 mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-muted rounded w-64 mb-1" />
              <div className="h-4 bg-gray-100 dark:bg-muted rounded w-full" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Findings</h1>
        <p className="text-muted-foreground dark:text-muted-foreground mt-1">Security issues found across all scans for this project</p>
      </div>

      {allFindings.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No findings yet"
          description="Run a scan to discover security issues for this project."
        />
      ) : (
        <>
          <FindingFilters
            selectedSeverities={selectedSeverities}
            onSeverityToggle={toggleSeverity}
            selectedStatuses={selectedStatuses}
            onStatusToggle={toggleStatus}
            resultCount={filteredFindings.length}
          />

          {filteredFindings.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No findings match your filters"
              description="Try adjusting your filter criteria to see more results."
            />
          ) : (
            <div className="flex flex-col gap-4">
              {filteredFindings.map((finding) => (
                <FindingCard
                  key={finding.findingId}
                  finding={finding}
                  orgId={orgId}
                  portfolioId={portfolioId}
                  projectId={projectId}
                  onDismiss={(findingId, action) => updateStatus.mutate({ findingId, status: action })}
                  onDelete={(findingId) => deleteFinding.mutate(findingId)}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
